// The Registration box on Book & pay (components/reg-lookup,
// docs/VEHICLE_LOOKUP.md), required since 20 Oct 2026. Pure helpers, so
// node --test can reach them.

// "ab12 cde" -> "AB12CDE"; '' for anything that cannot be a UK plate (2-7
// letters and digits, at least one of each). Same rule as the server's
// VehicleLookup.normalise.
export const plate = typed => {
  const p = (typed || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
  return /^(?=.*[A-Z])(?=.*\d).{2,7}$/.test(p) ? p : ''
}

// REQUIRED SINCE 20 OCT 2026. Robin: "pls make the registration plate entry
// mandatory". A FORMAT check, never a lookup: a plate the lookup cannot find
// (or the lookup down, or out of credit) still books. Spaces and case aside,
// every UK format: current AB12CDE, prefix A123BCD, suffix ABC123D, and
// dateless 1-3 letters and 1-4 digits either way round, which covers
// Northern Ireland's ABZ 1234 and 1234 AZ. Same pattern as the server's
// VehicleLookup::UK_PLATE.
export const UK_PLATE = /^([A-Z]{2}\d\d[A-Z]{3}|[A-Z]\d{1,3}[A-Z]{3}|[A-Z]{3}\d{1,3}[A-Z]|[A-Z]{1,3}\d{1,4}|\d{1,4}[A-Z]{1,3})$/

// null for a UK plate, else { reason, message }. reason: 'missing', or
// registration_invalid_format's bucket: 'too_long' (over 7), 'no_digit',
// 'no_letter', 'pattern' (letters and digits in no UK order).
export const regProblem = typed => {
  const p = (typed || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
  if (UK_PLATE.test(p)) return null
  const reason = !p ? 'missing' : p.length > 7 ? 'too_long' : !/\d/.test(p) ? 'no_digit' : !/[A-Z]/.test(p) ? 'no_letter' : 'pattern'
  return { reason, message: p ? 'Check your registration, e.g. AB12 CDE' : 'Add your registration' }
}

// How a plate is written: the current format with its space.
export const spaced = p => p.replace(/^([A-Z]{2}\d{2})([A-Z]{3})$/, '$1 $2')

// The car in words: "Black BMW X5".
export const carName = car => (car ? [car.colour, car.make, car.model].filter(Boolean).join(' ') : '')

// The one-line summary: "Black BMW X5, 2021".
export const carLine = car => (car ? [carName(car), car.year].filter(Boolean).join(', ') : '')

// What the order stores in make_model.
export const makeModel = car => (car ? [car.make, car.model].filter(Boolean).join(' ') : '')

// A gentle word when the car looks large and Small to medium is picked.
// Never changes the choice.
export const sizeHint = (car, size) => (car?.suggested_size === 'large' && size === 'small' ? 'This looks like a Large car.' : '')

// The looked-up car, kept in the form (not a field) so the Book & pay draft
// carries it across a reload or a return from Stripe (lib/booking-draft.js).
// Saved with its plate: { ...car, reg }.
export const CAR_LOOKUP = 'carLookup'

// FORM V3 (20 Oct 2026): where the car size came from, kept in the form
// (not a field) and in the draft: 'lookup' (the plate's car set it),
// 'manual' (picked, the plate gave no size) or 'changed' (picked after "Not
// right? Change size"). Only a size from the plate is ever replaced by the
// next plate's.
export const SIZE_SOURCE = 'sizeSource'

// What the box does when its plate goes from `prev` to `reg` (both as plate()
// reads them; prev undefined before the first run). `kept` is the car the
// draft brought back. null: nothing (an empty box on mount, so a plate the
// draft is about to put back is never cleared). Otherwise `car` to show at
// once (the kept one, when it is this plate's), `lookUp` when there is a
// plate and no car for it, `clear` when the customer changed a plate they
// had typed (the old make, model and colour must not ride the order).
export const regStep = (prev, reg, kept) => {
  if (reg === prev || (prev === undefined && !reg)) return null
  const car = reg && kept && kept.reg === reg ? kept : null
  return { car, lookUp: !!reg && !car, clear: !!prev }
}

// ---- Measuring the box (ANALYTICS.md "Registration box", 19 Oct 2026) ----
// Robin: "ok so add as optional and see how many use it". No event ever
// carries the plate, the make or the model.

// What a lookup came to, from GET /api/v2/vehicles: 200 found, 404 not_found
// (on the server that also covers the lookup being off or the provider
// failing: VehiclesController), anything else or no answer at all error.
export const lookupResult = status => (status === 200 ? 'found' : status === 404 ? 'not_found' : 'error')

// Whether a booking carries a plate the box would take (the pay events).
export const hasRegistration = typed => !!plate(typed)

// One per page view: registration_entered once per distinct plate, and the
// Large hint's shown / followed once per plate it showed for.
//   entered(reg, result, car, picked, ctx) -> the event's props, or null for
//     a plate already reported. picked: the car size at that moment
//     ('small' | 'large' | null); ctx: { form_variant, path }.
//   hint(reg, showing, size) -> 'shown' | 'followed' | null: shown when the
//     hint appears for a plate, followed when the size then goes to Large.
export const regTracker = () => {
  const entered = new Set()
  const shown = new Set()
  const followed = new Set()
  return {
    entered (reg, result, car, picked, ctx = {}) {
      if (!reg || entered.has(reg)) return null
      entered.add(reg)
      return {
        result,
        suggested_size: car?.suggested_size === 'large' ? 'large' : null,
        picked_size: picked || null,
        plate_index: entered.size,
        form_variant: ctx.form_variant || null,
        path: ctx.path || null
      }
    },
    hint (reg, showing, size) {
      if (!reg) return null
      if (showing && !shown.has(reg)) { shown.add(reg); return 'shown' }
      if (size === 'large' && shown.has(reg) && !followed.has(reg)) { followed.add(reg); return 'followed' }
      return null
    }
  }
}
